import type { ButtonHTMLAttributes, MouseEvent, ReactElement, ReactNode } from 'react';
import { forwardRef, isValidElement, useContext } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useIsLargerThanExtraWide } from '../../../styles/hooks/media';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { PassiveComponentContext } from '../../context';
import { Badge, BadgeSize } from '../../controls/badge/Badge';
import { Icon as CoreIcon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { withTooltip } from '../../tooltip/withTooltip';

import BorderGradient from './borderGradient/BorderGradient';
import { BorderTimer } from './borderTimer/BorderTimer';

import styles from './gameButton.module.css';

export enum GameButtonSize {
  SMALL = 'small',
  REGULAR = 'regular',
  LARGE = 'large',
}

export interface GameButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display on the button */
  icon?: keyof Icons | ReactElement;
  /** Whether the button is active or not */
  active?: boolean;
  /** Whether it has border or not */
  border?: boolean;
  /** Whether the button in an active state should pulse green */
  pulse?: boolean;
  /** A name of sound sample to be played */
  sound?: string;
  /** Label to display on the button */
  label?: ReactNode;
  /** Class name to apply to the label */
  labelClass?: string;
  /** Badge to display on the button */
  badge?: string | number | ReactElement<typeof Badge>;
  /** Whether the button should grow to fill its container */
  grow?: boolean;
  /** *(JUST for new Version)*
   *
   * If the size property is not set, it will be automatically calculated based on the screen size.
   *
   * (width >= 1366px) and (height >= 500px) = REGULAR
   *
   * (width >= 1512px) and (height >= 500px) = LARGE
   *
   * small = 32px
   * regular = 40px
   * large = 48px
   **/
  size?: GameButtonSize;
  /** *(JUST for new Version)*
   *
   * border gradient, if boolean === true, default gradient will be used.
   *
   * Otherwise use CSS string linear-gradient
   * *(full notation with direction and offset should be used)*
   **/
  borderGradient?: boolean | string;

  /** if present, will add border timer to the button with the given `timerDuration` duration in miliseconds */
  timerDuration?: number;
}

/**
 * Game button is a component that is used to display a game control or a chip stack control.
 * Built on the base of: `<button></button>`
 *
 * ## CSS Custom Properties
 * #### Old Version
 * | Name                   | Description                              | Default                    |
 * | ---------------------- | ---------------------------------------- | -------------------------- |
 * | **--borderBackground** | Border color (or gradient) of the button | `--game-border-background` |
 *
 * */
export const GameButton = forwardRef<HTMLDivElement, GameButtonProps>(function GameButtonFunc(
  { onClick, sound, ...props },
  forwardedRef
) {
  const isPassive = useContext(PassiveComponentContext);
  const audioTrigger = useAudioTrigger();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: sound ?? 'menuClick' });
    onClick?.(event);
  };

  return <GameButtonComponent isPassive={isPassive} onClick={handleClick} {...props} ref={forwardedRef} />;
});

export interface GameButtonComponentProps extends GameButtonProps {
  isPassive?: boolean;
}

export const GameButtonComponent = forwardRef<HTMLDivElement, GameButtonComponentProps>(
  function GameButtonComponentFunc(
    {
      icon,
      active,
      pulse,
      label,
      labelClass = '',
      badge,
      grow,
      className = '',
      isPassive,
      borderGradient,
      size,
      timerDuration,
      ...rest
    },
    forwardedRef
  ) {
    const mods = useModifiers(
      styles,
      'gameButton',
      {
        active,
        borderGradient: Boolean(borderGradient),
        grow,
        isPassive,
        pulse,
        [size as string]: true,
      },
      { withBaseClass: true, extraClassName: className }
    );

    const isLarge = useIsLargerThanExtraWide();

    const badgeSize = size === GameButtonSize.LARGE || (!size && isLarge) ? BadgeSize.Medium : BadgeSize.Small;

    return (
      <button type="button" className={mods} {...rest}>
        {borderGradient ? <BorderGradient gradient={borderGradient} size={size} label={label} /> : null}
        {timerDuration ? <BorderTimer duration={timerDuration} /> : null}
        <span className={styles.gameButton__innerWrapper} ref={forwardedRef}>
          {renderIcon(icon)}
          {label ? <span className={labelClass}>{label}</span> : null}
        </span>
        {renderBadge(badge, badgeSize)}
      </button>
    );
  }
);

export const GameButtonWithTooltip = withTooltip(GameButton);

GameButtonWithTooltip.displayName = 'gameButtonWithTooltip';

// Utils

const renderIcon = (icon?: keyof Icons | ReactElement): ReactElement | null => {
  if (isValidElement(icon)) {
    return <div className={styles.gameButton__icon}>{icon}</div>;
  }

  if (typeof icon === 'string') {
    return <CoreIcon icon={icon} className={styles.gameButton__icon} />;
  }

  return null;
};

const renderBadge = (
  badge: string | number | ReactElement | undefined,
  size: BadgeSize.Small | BadgeSize.Medium
): ReactElement | null => {
  if (isValidElement(badge)) {
    return <span className={styles.gameButton__badge}>{badge}</span>;
  }

  if (badge && (typeof badge === 'string' || typeof badge === 'number')) {
    return <Badge className={styles.gameButton__badge} size={size} grow="start" value={badge} impact />;
  }

  return null;
};
